// https://github.com/phatnguyenuit/react-otp-input-scratch

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable prefer-destructuring */
/* eslint-disable react/no-array-index-key */
import {
  memo, useState, useCallback, useRef, useLayoutEffect, FC, InputHTMLAttributes,
} from 'react'

import usePrevious from '@/hooks/usePrevious'

type SingleOTPInputComponentProps = InputHTMLAttributes<HTMLInputElement> & {
  focus: boolean
  autoFocus: boolean
}

const SingleOTPInputComponent:FC<SingleOTPInputComponentProps> = ({
  focus,
  autoFocus = false,
  ...props
}) => {
  const inputRef = useRef(null)
  const prevFocus = usePrevious(!!focus)

  useLayoutEffect(() => {
    if (inputRef.current) {
      if (focus && autoFocus) {
        inputRef.current.focus()
      }
      if (focus && autoFocus && focus !== prevFocus) {
        inputRef.current.focus()
        inputRef.current.select()
      }
    }
  }, [autoFocus, focus, prevFocus])

  return <input data-type="otp" ref={inputRef} {...props} />
}

const SingleInput = memo(SingleOTPInputComponent)

type OTPInputComponentProps = {
  length: number
  onChangeOTP: (otp: string) => void
  autoFocus?: boolean
  isNumberInput?: boolean
  disabled?: boolean
  isError?: boolean
  errorText?: string
  label?: string
}
export const OTPInputComponent: FC<OTPInputComponentProps> = props => {
  const {
    length,
    isNumberInput = false,
    autoFocus = false,
    disabled = false,
    onChangeOTP,
    label,
    isError = false,
    errorText,
    ...rest
  } = props

  const [activeInput, setActiveInput] = useState(0)
  const [otpValues, setOTPValues] = useState((Array(length)).fill(''))

  // Helper to return OTP from inputs
  const handleOtpChange = useCallback(
    otp => {
      const otpValue = otp.join('')
      onChangeOTP(otpValue)
    },
    [onChangeOTP],
  )

  // Helper to return value with the right type: 'text' or 'number'
  const getRightValue = useCallback(
    str => {
      const changedValue = str

      if (!isNumberInput || !changedValue) {
        return changedValue
      }

      return Number(changedValue) >= 0 ? changedValue : ''
    },
    [isNumberInput],
  )

  // Change OTP value at focussing input
  const changeCodeAtFocus = useCallback(
    str => {
      const updatedOTPValues = [...otpValues]
      updatedOTPValues[activeInput] = str[0] || ''
      setOTPValues(updatedOTPValues)
      handleOtpChange(updatedOTPValues)
    },
    [activeInput, handleOtpChange, otpValues],
  )

  // Focus `inputIndex` input
  const focusInput = useCallback(
    inputIndex => {
      const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0)
      setActiveInput(selectedIndex)
    },
    [length],
  )

  const focusPrevInput = useCallback(() => {
    focusInput(activeInput - 1)
  }, [activeInput, focusInput])

  const focusNextInput = useCallback(() => {
    focusInput(activeInput + 1)
  }, [activeInput, focusInput])

  // Handle onFocus input
  const handleOnFocus = useCallback(
    index => () => {
      focusInput(index)
    },
    [focusInput],
  )

  // Handle onChange value for each input
  const handleOnChange = useCallback(
    e => {
      const val = getRightValue(e.currentTarget.value)
      if (!val) {
        e.preventDefault()
        return
      }
      changeCodeAtFocus(val)
      focusNextInput()
    },
    [changeCodeAtFocus, focusNextInput, getRightValue],
  )

  // Handle onBlur input
  const onBlur = useCallback(() => {
    setActiveInput(-1)
  }, [])

  // Handle onKeyDown input
  const handleOnKeyDown = useCallback(
    e => {
      const pressedKey = e.key

      switch (pressedKey) {
        case 'Backspace':
        case 'Delete': {
          e.preventDefault()
          if (otpValues[activeInput]) {
            changeCodeAtFocus('')
          } else {
            focusPrevInput()
          }
          break
        }
        case 'ArrowLeft': {
          e.preventDefault()
          focusPrevInput()
          break
        }
        case 'ArrowRight': {
          e.preventDefault()
          focusNextInput()
          break
        }
        default: {
          if (pressedKey.match(/^[^a-zA-Z0-9]$/)) {
            e.preventDefault()
          }

          break
        }
      }
    },
    [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues],
  )

  const handleOnPaste = useCallback(
    e => {
      e.preventDefault()
      const pastedData = e.clipboardData
        .getData('text/plain')
        .replace(/\s/g, '')
        .trim()
        .slice(0, length - activeInput)
        .split('')

      if (pastedData) {
        let nextFocusIndex = 0
        const updatedOTPValues = [...otpValues]
        updatedOTPValues.forEach((val, index) => {
          if (index >= activeInput) {
            const changedValue = getRightValue(pastedData.shift() || val)
            if (changedValue) {
              updatedOTPValues[index] = changedValue
              nextFocusIndex = index
            }
          }
        })
        setOTPValues(updatedOTPValues)
        setActiveInput(Math.min(nextFocusIndex + 1, length - 1))
        handleOtpChange(updatedOTPValues)
      }
    },
    [activeInput, getRightValue, length, otpValues],
  )

  return (
    <div {...rest} className="otp-field field-container flex flex-col [&_select]:placeholder:text-gray-4 [&_input]:placeholder:text-gray-4 [&_textarea]:placeholder:text-gray-4">
      { label && <label className="text-gray-3">{ label }</label> }
      <div className="inputs flex gap-2">
        {Array(length)
          .fill('')
          .map((_, index) => (
            <SingleInput
              key={`SingleInput-${index}`}
              type={isNumberInput ? 'number' : 'text'}
              focus={activeInput === index}
              value={otpValues && otpValues[index]}
              autoFocus={autoFocus}
              onFocus={handleOnFocus(index)}
              onChange={handleOnChange}
              onKeyDown={handleOnKeyDown}
              onBlur={onBlur}
              onPaste={handleOnPaste}
              disabled={disabled}
              placeholder="0"
              // `disabled:bg-gray-6` re-states the fill: the base-layer `input:disabled`
              // (0-1-1) out-ranks a plain `bg-gray-6`.
              className="border border-solid border-gray-5 bg-gray-6 text-gray-1 disabled:bg-gray-6 mb-0 h-[56px] max-w-[80px] p-4 text-center font-[PolySans-Median] text-[20px] font-semibold not-italic leading-[100%]"
            />
          ))}

      </div>
      { isError && (
        <div className="error mt-[8px] font-[Switzer] text-[12px] font-normal not-italic leading-[110%] [color:#e4635c]">
          {errorText}
        </div>
      )}
    </div>
  )
}

const OTPInput = memo(OTPInputComponent)

export default OTPInput
